import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {forkJoin, of, Subject} from 'rxjs';
import {catchError, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {FeeService} from '../service/fee.service';
import {
  CardOption,
  FeeDashboard,
  FeeDashboardPeriod,
  FeeGroupResponse,
  FeeMemberResponse,
  FeeSubgroupResponse,
  MemberProfileResponse,
  UserIndustryResponse
} from '../interface/fee.interface';
import {UserService} from '../service/user.service';
import {formatApiDateString, parseApiDateString} from '../utils/date.utils';
import {DEFAULT_USER_ICON, OVERVIEW_VALID_TABS} from '../constants/global.constants';
import {
  FEE_PAYMENT_FILTER_DIALOG_ACTION,
  PAYMENT_FILTER_STATUS
} from '../constants/fee-payment-filter.constants';
import {FEE_PAYMENT_STATUS} from '../constants/fee-payment-status.constants';
import {FeeStateService} from '../service/fee-state.service';
import {
  SubgroupDeleteDialogComponent,
  SubgroupDeleteDialogData
} from '../fee-subgroup-delete-dialog/fee-subgroup-delete-dialog.component';
import {
  GroupDeleteDialogComponent,
  GroupDeleteDialogData
} from '../fee-group-delete-dialog/fee-group-delete-dialog.component';
import {TranslateService} from '@ngx-translate/core';
import {
  EMPTY_PAYMENT_FILTER_VALUE,
  FeePaymentFilterDialogComponent,
  FeePaymentFilterDialogData,
  FeePaymentFilterDialogResult,
  FeePaymentFilterValue
} from '../fee-payment-filter-dialog/fee-payment-filter-dialog.component';

type MemberRow = { id: number; subgroupId: number; name: string; frequency: string; discount: string; due: string; total: string; status: string; active: boolean };
type OverviewStatus = 'COMPLETED' | 'NOT_STARTED' | 'IN_PROGRESS';
type OverviewTab = 'ALL_MEMBERS' | 'GROUPS' | 'SUBGROUPS' | 'MENTORS';
type OverviewRow = {
  subgroupId: number;
  groupId: number;
  subgroupName: string;
  parentGroupName: string;
  membersQty: number;
  startEndDate: string;
  mentorName: string;
  status: OverviewStatus;
};
type GroupOverviewRow = {
  groupId: number;
  groupName: string;
  subgroupNames: string;
  membersQty: number;
};
type MentorOverviewRow = {
  mentorName: string;
  mentorPhone: string;
  mentorEmail: string;
  groupLinks: { id: number; name: string }[];
  subgroupLinks: { id: number; groupId: number; name: string }[];
  groups: string;
  subgroups: string;
  subgroupTotal: number;
  amountTotal: number;
  subgroupIds: number[];
};
type AllMembersPaymentRow = {
  memberId: number;
  memberName: string;
  memberActive: boolean;
  passportId: string;
  groupId: number;
  groupName: string;
  subgroupId: number;
  subgroupName: string;
  mentorName: string;
  period: string;
  paid: number;
  due: number;
  status: string;
};
type DashboardPeriodOption = {
  label: string;
  value: FeeDashboardPeriod;
};

const MIN_CARD_ID_LENGTH = 0;

@Component({
  selector: 'app-fee-group-detail',
  templateUrl: './fee-group-detail.component.html',
  styleUrls: ['./fee-group-detail.component.scss']
})
export class FeeGroupDetailComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private readonly overviewDeleteDialogWidth = '420px';

  userIndustry: UserIndustryResponse | null = null;
  dashboard: FeeDashboard = { paidCount: 0, paid: 0, unpaidCount: 0, unpaid: 0, overdueCount: 0, overdue: 0 };
  readonly invoicePeriodOptions: DashboardPeriodOption[] = [
    { label: 'fee.dashboard.this_week', value: 'THIS_WEEK' },
    { label: 'fee.dashboard.this_month', value: 'THIS_MONTH' },
    { label: 'fee.dashboard.this_year', value: 'THIS_YEAR' },
  ];
  selectedDashboardPeriod: DashboardPeriodOption = this.invoicePeriodOptions[0];
  invoiceSummaryExpanded = false;
  invoiceSummaryTitle = '';
  feeGroups: FeeGroupResponse[] = [];
  searchQuery = '';
  avatarUrl: string = DEFAULT_USER_ICON;
  activeGroupIndex = 0;
  activeSubgroupIndex = -1;
  isMembersOpen = false;
  memberRows: MemberRow[] = [];
  activeOverviewTab: OverviewTab = 'ALL_MEMBERS';
  allMembersPaymentRows: AllMembersPaymentRow[] = [];
  paymentFilters: FeePaymentFilterValue = {...EMPTY_PAYMENT_FILTER_VALUE};
  openedGroupMenuId: number | null = null;
  openedSubgroupMenuId: number | null = null;
  openedMentorMenuKey: string | null = null;
  subgroupMembersSearchQuery = '';
  readonly loadMorePageSize = 10;
  allMembersPaymentVisibleCount = this.loadMorePageSize;
  subgroupsVisibleCount = this.loadMorePageSize;
  groupsVisibleCount = this.loadMorePageSize;
  mentorsVisibleCount = this.loadMorePageSize;
  subgroupMembersVisibleCount = this.loadMorePageSize;
  isLoading = false;

  industryId!: number;
  showWelcome = false;

  newMemberForm!: FormArray;
  newMemberMetas: { saved: boolean; expanded: boolean }[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    readonly feeService: FeeService,
    private readonly userService: UserService,
    private readonly feeStateService: FeeStateService,
    private readonly dialog: MatDialog,
    private readonly fb: FormBuilder,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.newMemberForm = this.fb.array([]);
    this.loadInvoiceSummaryTitle();
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadInvoiceSummaryTitle());

    if (this.route.snapshot.queryParamMap.get('new') === '1') {
      this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
    }

    const tabParam = this.route.snapshot.queryParamMap.get('tab') as OverviewTab;
    if ((OVERVIEW_VALID_TABS as readonly string[]).includes(tabParam)) {
      this.activeOverviewTab = tabParam;
    }

    this.route.paramMap
      .pipe(
        map(params => Number(params.get('id'))),
        filter(id => !Number.isNaN(id)),
        distinctUntilChanged(),
        tap(id => {
          this.industryId = id;
          this.userIndustry = null;
          this.feeGroups = [];
          this.activeGroupIndex = 0;
          this.activeSubgroupIndex = -1;
          this.isMembersOpen = false;
        }),
        switchMap(id =>
          forkJoin({
            userIndustry: this.feeService.getUserIndustryById(id),
            feeGroups: this.feeService.getFeeGroups(id),
            dashboard: this.feeService.getDashboard(this.selectedDashboardPeriod.value, id)
          })
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ userIndustry, feeGroups, dashboard }) => {
        this.userIndustry = userIndustry;
        this.feeStateService.setActiveUserIndustry(userIndustry);
        this.feeStateService.setHasGroups(feeGroups.length > 0);
        this.feeGroups = feeGroups;
        this.dashboard = dashboard;
        this.activeGroupIndex = 0;
        this.activeSubgroupIndex = -1;
        this.isMembersOpen = false;
        this.resetVisibleCounts();
        this.showWelcome = !this.hasLinkedCard(userIndustry);
        this.loadMembersForCurrentSelection();
        this.loadAllMembersPaymentRows();
      });

    this.userService.updatedProfileImageUrl
      .pipe(takeUntil(this.destroy$))
      .subscribe(url => { this.avatarUrl = url ?? DEFAULT_USER_ICON; });
  }

  onWelcomeSetup(card: CardOption): void {
    this.feeService.updateIndustryCard(this.industryId, card.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: updated => {
          if (updated) {
            this.userIndustry = updated;
            this.feeStateService.setActiveUserIndustry(updated);
          } else if (this.userIndustry) {
            this.userIndustry = { ...this.userIndustry, cardId: String(card.id) };
          }
          this.showWelcome = false;
          this.router.navigate(['groups', 'create'], {
            relativeTo: this.route,
            queryParams: { returnUrl: this.router.url }
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.feeStateService.setActiveUserIndustry(null);
    this.feeStateService.setHasGroups(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openedGroupMenuId = null;
    this.openedSubgroupMenuId = null;
    this.openedMentorMenuKey = null;
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.isLoading) {
      this.loadMore();
    }
  }

  get activeGroup(): FeeGroupResponse | null {
    return this.feeGroups[this.activeGroupIndex] ?? null;
  }

  get activeSubgroup(): FeeSubgroupResponse | null {
    if (this.activeSubgroupIndex < 0 || !this.activeGroup) return null;
    return this.activeGroup.subgroups[this.activeSubgroupIndex] ?? null;
  }

  get overviewRows(): OverviewRow[] {
    const rows: OverviewRow[] = [];
    const query = this.searchQuery.trim().toLowerCase();

    for (const group of this.feeGroups) {
      for (const subgroup of group.subgroups) {
        const row: OverviewRow = {
          subgroupId: subgroup.id,
          groupId: group.id,
          subgroupName: subgroup.name,
          parentGroupName: group.name,
          membersQty: subgroup.memberCount ?? 0,
          startEndDate: this.formatDateRange(subgroup),
          mentorName: subgroup.mentorName || '—',
          status: this.resolveSubgroupStatus(subgroup),
        };
        if (!query || this.matchesOverviewQuery(row, query)) {
          rows.push(row);
        }
      }
    }

    return rows;
  }

  get groupOverviewRows(): GroupOverviewRow[] {
    const query = this.searchQuery.trim().toLowerCase();
    const rows = this.feeGroups.map(group => {
      const subgroupNames = group.subgroups.map(s => s.name).join(', ');
      const membersQty = group.subgroups.reduce((sum, s) => sum + (s.memberCount || 0), 0);
      return {
        groupId: group.id,
        groupName: group.name,
        subgroupNames: subgroupNames || '—',
        membersQty,
      };
    });
    if (!query) return rows;
    return rows.filter(row =>
      row.groupName.toLowerCase().includes(query)
      || row.subgroupNames.toLowerCase().includes(query)
      || String(row.membersQty).includes(query)
    );
  }

  get mentorOverviewRows(): MentorOverviewRow[] {
    const query = this.searchQuery.trim().toLowerCase();
    const mentorsMap = new Map<string, {
      groups: Map<number, string>;
      subgroups: Map<number, { name: string; groupId: number }>;
      subgroupIdSet: Set<number>;
      display: string;
      phone: string;
      email: string;
    }>();

    for (const group of this.feeGroups) {
      for (const subgroup of group.subgroups) {
        const mentorDisplay = subgroup.mentorName?.trim() || '';
        if (!mentorDisplay) continue;
        const key = mentorDisplay.toLowerCase();
        const current = mentorsMap.get(key) || {
          groups: new Map<number, string>(),
          subgroups: new Map<number, { name: string; groupId: number }>(),
          subgroupIdSet: new Set<number>(),
          display: mentorDisplay,
          phone: subgroup.mentorPhone || '',
          email: subgroup.mentorEmail || '',
        };
        if (!current.phone && subgroup.mentorPhone) current.phone = subgroup.mentorPhone;
        if (!current.email && subgroup.mentorEmail) current.email = subgroup.mentorEmail;
        current.groups.set(group.id, group.name);
        current.subgroups.set(subgroup.id, { name: subgroup.name, groupId: group.id });
        current.subgroupIdSet.add(subgroup.id);
        mentorsMap.set(key, current);
      }
    }

    const rows = Array.from(mentorsMap.values()).map(item => ({
      mentorName: item.display,
      mentorPhone: item.phone || '—',
      mentorEmail: item.email || '—',
      groupLinks: Array.from(item.groups.entries()).map(([id, name]) => ({ id, name })),
      subgroupLinks: Array.from(item.subgroups.entries()).map(([id, value]) => ({
        id,
        groupId: value.groupId,
        name: value.name
      })),
      groups: Array.from(item.groups.values()).join(', '),
      subgroups: Array.from(item.subgroups.values()).map(value => value.name).join(', '),
      subgroupTotal: item.subgroups.size,
      amountTotal: this.getMentorAmountTotal(item.display),
      subgroupIds: Array.from(item.subgroupIdSet),
    }));

    if (!query) return rows;
    return rows.filter(row =>
      row.mentorName.toLowerCase().includes(query)
      || row.mentorPhone.toLowerCase().includes(query)
      || row.mentorEmail.toLowerCase().includes(query)
      || row.groups.toLowerCase().includes(query)
      || row.subgroups.toLowerCase().includes(query)
      || String(row.subgroupTotal).includes(query)
      || String(row.amountTotal).includes(query)
    );
  }

  get subgroupCount(): number {
    return this.feeGroups.reduce((acc, g) => acc + g.subgroups.length, 0);
  }

  get filteredAllMembersPaymentRows(): AllMembersPaymentRow[] {
    const query = this.searchQuery.trim().toLowerCase();
    return this.allMembersPaymentRows.filter(row =>
      this.matchesPaymentSearch(row, query) && this.matchesPaymentFilters(row)
    );
  }

  get isPaymentFilterActive(): boolean {
    const f = this.paymentFilters;
    return f.groupId !== null
      || f.subgroupId !== null
      || !!f.periodFrom
      || !!f.periodTo
      || f.status !== PAYMENT_FILTER_STATUS.ALL;
  }

  private matchesPaymentSearch(row: AllMembersPaymentRow, query: string): boolean {
    if (!query) return true;
    return row.memberName.toLowerCase().includes(query)
      || row.passportId.toLowerCase().includes(query)
      || row.groupName.toLowerCase().includes(query)
      || row.subgroupName.toLowerCase().includes(query)
      || row.period.toLowerCase().includes(query)
      || row.status.toLowerCase().includes(query);
  }

  private matchesPaymentFilters(row: AllMembersPaymentRow): boolean {
    const f = this.paymentFilters;
    if (f.groupId !== null && row.groupId !== f.groupId) return false;
    if (f.subgroupId !== null && row.subgroupId !== f.subgroupId) return false;
    if (f.status !== PAYMENT_FILTER_STATUS.ALL && row.status !== f.status) return false;
    if (f.periodFrom || f.periodTo) {
      const periodDate = this.parsePeriodToDate(row.period);
      if (!periodDate) return false;
      if (f.periodFrom && periodDate < this.startOfDay(f.periodFrom)) return false;
      if (f.periodTo && periodDate > this.endOfDay(f.periodTo)) return false;
    }
    return true;
  }

  get displayedAllMembersPaymentRows(): AllMembersPaymentRow[] {
    return this.filteredAllMembersPaymentRows.slice(0, this.allMembersPaymentVisibleCount);
  }

  get displayedOverviewRows(): OverviewRow[] {
    return this.overviewRows.slice(0, this.subgroupsVisibleCount);
  }

  get displayedGroupOverviewRows(): GroupOverviewRow[] {
    return this.groupOverviewRows.slice(0, this.groupsVisibleCount);
  }

  get displayedMentorOverviewRows(): MentorOverviewRow[] {
    return this.mentorOverviewRows.slice(0, this.mentorsVisibleCount);
  }

  get filteredSubgroupMembers(): MemberRow[] {
    const query = this.subgroupMembersSearchQuery.trim().toLowerCase();
    if (!query) return this.memberRows;
    return this.memberRows.filter(row =>
      row.name.toLowerCase().includes(query)
      || row.frequency.toLowerCase().includes(query)
      || row.status.toLowerCase().includes(query)
    );
  }

  get displayedSubgroupMembers(): MemberRow[] {
    return this.filteredSubgroupMembers.slice(0, this.subgroupMembersVisibleCount);
  }

  get hasMoreAllMembersPaymentRows(): boolean {
    return this.allMembersPaymentVisibleCount < this.filteredAllMembersPaymentRows.length;
  }

  get hasMoreSubgroupRows(): boolean {
    return this.subgroupsVisibleCount < this.overviewRows.length;
  }

  get hasMoreGroupRows(): boolean {
    return this.groupsVisibleCount < this.groupOverviewRows.length;
  }

  get hasMoreMentorRows(): boolean {
    return this.mentorsVisibleCount < this.mentorOverviewRows.length;
  }

  get hasMoreSubgroupMembers(): boolean {
    return this.subgroupMembersVisibleCount < this.filteredSubgroupMembers.length;
  }

  get subgroupPaidCount(): number {
    return this.memberRows.filter(r => r.status === FEE_PAYMENT_STATUS.PAID).length;
  }

  get subgroupPendingCount(): number {
    return this.memberRows.filter(r => r.status !== FEE_PAYMENT_STATUS.PAID).length;
  }


  get mentorCount(): number {
    const mentors = new Set<string>();
    for (const g of this.feeGroups) {
      for (const s of g.subgroups) {
        if (s.mentorName) mentors.add(s.mentorName.trim().toLowerCase());
      }
    }
    return mentors.size;
  }

  selectOverviewTab(tab: OverviewTab): void {
    if (this.showWelcome) return;
    this.openedGroupMenuId = null;
    this.openedSubgroupMenuId = null;
    this.openedMentorMenuKey = null;
    this.resetVisibleCounts();
    this.setOverviewTab(tab);
  }

  onOverviewSearchChange(value: string): void {
    this.searchQuery = value;
    this.resetVisibleCounts();
  }

  openPaymentFilterDialog(): void {
    if (this.showWelcome || this.activeOverviewTab !== 'ALL_MEMBERS') return;

    const groups = this.feeGroups.map(group => ({id: group.id, name: group.name}));
    const subgroups = this.feeGroups.flatMap(group =>
      group.subgroups.map(subgroup => ({id: subgroup.id, name: subgroup.name, groupId: group.id}))
    );

    const config = new MatDialogConfig<FeePaymentFilterDialogData>();
    config.panelClass = 'menu-filter-dialog-panel';
    config.width = '460px';
    config.maxWidth = '95vw';
    config.data = {groups, subgroups, current: {...this.paymentFilters}};

    this.dialog.open(FeePaymentFilterDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: FeePaymentFilterDialogResult | null) => {
        if (!result) return;
        this.paymentFilters = result.action === FEE_PAYMENT_FILTER_DIALOG_ACTION.RESET
          ? {...EMPTY_PAYMENT_FILTER_VALUE}
          : result.filters;
        this.allMembersPaymentVisibleCount = this.loadMorePageSize;
      });
  }

  loadMore(): void {
    if (this.activeOverviewTab === 'ALL_MEMBERS' && this.hasMoreAllMembersPaymentRows) {
      this.allMembersPaymentVisibleCount += this.loadMorePageSize;
      return;
    }

    if (this.activeOverviewTab === 'SUBGROUPS') {
      if (this.isMembersOpen && this.hasMoreSubgroupMembers) {
        this.subgroupMembersVisibleCount += this.loadMorePageSize;
        return;
      }
      if (this.hasMoreSubgroupRows) {
        this.subgroupsVisibleCount += this.loadMorePageSize;
      }
      return;
    }

    if (this.activeOverviewTab === 'GROUPS' && this.hasMoreGroupRows) {
      this.groupsVisibleCount += this.loadMorePageSize;
      return;
    }

    if (this.activeOverviewTab === 'MENTORS' && this.hasMoreMentorRows) {
      this.mentorsVisibleCount += this.loadMorePageSize;
    }
  }

  private setOverviewTab(tab: OverviewTab): void {
    this.activeOverviewTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  get isOverviewMenuOpen(): boolean {
    return this.openedGroupMenuId !== null
      || this.openedSubgroupMenuId !== null
      || this.openedMentorMenuKey !== null;
  }

  toggleGroupMenu(event: Event, groupId: number): void {
    event.stopPropagation();
    this.openedSubgroupMenuId = null;
    this.openedGroupMenuId = this.openedGroupMenuId === groupId ? null : groupId;
  }

  toggleSubgroupMenu(event: Event, subgroupId: number): void {
    event.stopPropagation();
    this.openedGroupMenuId = null;
    this.openedSubgroupMenuId = this.openedSubgroupMenuId === subgroupId ? null : subgroupId;
  }

  editGroupFromOverview(groupId: number): void {
    this.openedGroupMenuId = null;
    this.router.navigate(['/fees', this.industryId, 'groups', 'create'], {
      queryParams: { groupId, mode: 'edit', returnUrl: this.router.url }
    });
  }

  deleteGroupFromOverview(groupId: number): void {
    this.openedGroupMenuId = null;
    const targetGroup = this.feeGroups.find((feeGroup) => feeGroup.id === groupId);
    if (!targetGroup) return;

    const groupHasLinkedData = targetGroup.subgroups.length > 0
      || targetGroup.subgroups.some((subgroup) => (subgroup.memberCount ?? 0) > 0);

    const deleteDialogConfig = new MatDialogConfig<GroupDeleteDialogData>();
    deleteDialogConfig.data = {
      groupId: targetGroup.id,
      groupName: targetGroup.name,
      hasData: groupHasLinkedData
    };
    deleteDialogConfig.width = this.overviewDeleteDialogWidth;

    this.dialog.open(GroupDeleteDialogComponent, deleteDialogConfig)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((wasDeleted: boolean | undefined) => {
        if (wasDeleted) this.reloadFeeGroups();
      });
  }

  editSubgroupFromOverview(subgroupId: number): void {
    if (this.isSubgroupCompleted(subgroupId)) return;
    this.openedSubgroupMenuId = null;
    const ownerGroup = this.findGroupBySubgroupId(subgroupId);
    if (!ownerGroup) return;
    this.router.navigate(['/fees', this.industryId, 'groups', 'create'], {
      queryParams: {
        mode: 'edit-subgroup',
        groupId: ownerGroup.id,
        subgroupId,
        returnUrl: this.router.url
      }
    });
  }

  deleteSubgroupFromOverview(subgroupId: number): void {
    this.openedSubgroupMenuId = null;
    const subgroup = this.findSubgroupById(subgroupId);
    if (!subgroup) return;
    const config = new MatDialogConfig<SubgroupDeleteDialogData>();
    config.data = {
      subgroupId: subgroup.id,
      subgroupName: subgroup.name,
      hasData: (subgroup.memberCount ?? 0) > 0
    };
    config.width = this.overviewDeleteDialogWidth;
    this.dialog.open(SubgroupDeleteDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((deleted: boolean | undefined) => {
        if (deleted) this.reloadFeeGroups();
      });
  }

  toggleMentorMenu(event: Event, mentorName: string): void {
    event.stopPropagation();
    this.openedGroupMenuId = null;
    this.openedSubgroupMenuId = null;
    const key = mentorName.toLowerCase();
    this.openedMentorMenuKey = this.openedMentorMenuKey === key ? null : key;
  }

  editMentorFromOverview(row: MentorOverviewRow): void {
    this.openedMentorMenuKey = null;
    if (row.subgroupIds.length === 0) return;
    const ownerGroup = this.findGroupBySubgroupId(row.subgroupIds[0]);
    if (!ownerGroup) return;
    this.router.navigate(['/fees', this.industryId, 'groups', 'create'], {
      queryParams: {
        mode: 'edit-subgroup',
        groupId: ownerGroup.id,
        subgroupId: row.subgroupIds[0],
        returnUrl: this.router.url
      }
    });
  }

  deleteMentorFromOverview(row: MentorOverviewRow): void {
    this.openedMentorMenuKey = null;
    const updates = row.subgroupIds
      .map(id => this.findSubgroupById(id))
      .filter((sub): sub is FeeSubgroupResponse => sub !== null)
      .map(sub => this.feeService.updateSubgroup(sub.id, {
        name: sub.name,
        price: sub.price,
        discountType: sub.discountType ?? null,
        discountValue: sub.discountValue,
        mentorName: null,
        mentorPhone: null,
        mentorEmail: null,
        startDate: sub.startDate,
        endDate: sub.endDate ?? null,
        frequency: sub.frequency,
        reason: sub.reason ?? null
      }));
    if (updates.length === 0) return;
    forkJoin(updates)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: () => this.reloadFeeGroups() });
  }

  openGroupPage(groupId: number): void {
    this.router.navigate(['/fees', this.industryId, 'groups', groupId]);
  }

  openSubgroupPage(groupId: number, subgroupId: number): void {
    this.router.navigate(['/fees', this.industryId, 'groups', groupId], {
      queryParams: { subgroupId }
    });
  }

  addSubgroup(): void {
    if (this.showWelcome) return;
    this.router.navigate(['/fees', this.industryId, 'groups', 'create']);
  }

  addMember(): void {
    if (!this.activeGroup) return;
    this.router.navigate(['/fees', this.industryId, 'add-member'], {
      queryParams: {
        groupId: this.activeGroup.id,
        subgroupId: this.activeSubgroup?.id,
        returnUrl: this.router.url
      }
    });
  }

  addMemberInline(): void {
    if (!this.activeGroup || !this.activeSubgroup) return;

    this.newMemberForm.push(this.fb.group({
      groupId: [this.activeGroup.id, Validators.required],
      subgroupId: [this.activeSubgroup.id, Validators.required],
      userName: ['', [Validators.required, Validators.maxLength(50)]],
      passportId: ['', [Validators.required, Validators.maxLength(20)]],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9+\-\s().]+$/)]],
      email: ['', [Validators.required, Validators.email]],
      frequency: ['', Validators.required],
      dateOfBirth: [null]
    }));

    this.newMemberMetas.push({ saved: false, expanded: false });
  }

  removeMemberInline(index: number): void {
    this.newMemberForm.removeAt(index);
    this.newMemberMetas = this.newMemberMetas.filter((_, i) => i !== index);
  }

  saveMemberInline(index: number): void {
    const memberCtrl = this.newMemberForm.at(index);
    memberCtrl.markAllAsTouched();

    if (memberCtrl.invalid) return;

    const value = memberCtrl.value;
    const payload = {
      name: value.userName,
      passportId: value.passportId,
      phone: value.phone || null,
      email: value.email || null,
      frequency: value.frequency,
      dateOfBirth: value.dateOfBirth || null
    };

    this.feeService.addMemberToSubgroup(value.subgroupId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.newMemberMetas[index] = { saved: true, expanded: false };
          this.loadMembersForCurrentSelection();
        },
        error: (err) => {
          console.error('Failed to save member:', err);
        }
      });
  }

  toggleMemberInline(index: number): void {
    this.newMemberMetas[index].expanded = !this.newMemberMetas[index].expanded;
  }

  getMemberControl(index: number, field: string): FormControl {
    return this.newMemberForm.at(index).get(field) as FormControl;
  }

  toggleInvoiceSummary(): void {
    this.invoiceSummaryExpanded = !this.invoiceSummaryExpanded;
  }

  selectDashboardPeriod(option: DashboardPeriodOption): void {
    if (this.showWelcome) return;
    if (option.value === this.selectedDashboardPeriod.value) return;
    this.selectedDashboardPeriod = option;
    this.loadDashboard();
  }

  frequencyLabel(value: string | undefined): string {
    if (!value) return '';
    const key = `fee.frequency_types.${value.toUpperCase()}`;
    const translated = this.translateService.instant(key);
    return translated !== key ? translated : value;
  }

  formatDateRange(sub: FeeSubgroupResponse): string {
    if (!sub.startDate) return '—';
    const from = formatApiDateString(sub.startDate, '.');
    return sub.endDate ? `${from} – ${formatApiDateString(sub.endDate, '.')}` : from;
  }

  statusLabel(status: OverviewStatus): string {
    if (status === 'COMPLETED') return this.translateService.instant('fee.status.completed');
    if (status === 'NOT_STARTED') return this.translateService.instant('fee.status.not_started');
    return this.translateService.instant('fee.status.in_progress');
  }

  statusClass(status: OverviewStatus): string {
    if (status === 'COMPLETED') return 'status-completed';
    if (status === 'NOT_STARTED') return 'status-in-progress';
    return 'status-not-started';
  }

  paymentStatusLabel(status: string): string {
    const key = (status || '').toUpperCase();
    if (key === FEE_PAYMENT_STATUS.PAID) return this.translateService.instant('payment_status.paid');
    if (key === FEE_PAYMENT_STATUS.PENDING) return this.translateService.instant('payment_status.pending');
    if (key === FEE_PAYMENT_STATUS.OVERDUE) return this.translateService.instant('fee.status.overdue');
    if (key === FEE_PAYMENT_STATUS.ENDED) return this.translateService.instant('fee.status.ended');
    return status || '';
  }

  private loadMembersForCurrentSelection(): void {
    if (!this.activeGroup) {
      this.memberRows = [];
      this.subgroupMembersVisibleCount = this.loadMorePageSize;
      return;
    }

    if (this.activeSubgroup) {
      this.feeService.getMembersBySubgroup(this.activeSubgroup.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(members => {
          this.memberRows = members.map(member => this.toMemberRow(member));
          this.subgroupMembersVisibleCount = this.loadMorePageSize;
        });
      return;
    }

    const subgroupIds = this.activeGroup.subgroups.map(sub => sub.id);
    if (subgroupIds.length === 0) {
      this.memberRows = [];
      this.subgroupMembersVisibleCount = this.loadMorePageSize;
      return;
    }

    const requests = subgroupIds.map(id => this.feeService.getMembersBySubgroup(id));
    forkJoin(requests.length > 0 ? requests : [of([] as FeeMemberResponse[])])
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.memberRows = response.flat().map(member => this.toMemberRow(member));
        this.subgroupMembersVisibleCount = this.loadMorePageSize;
      });
  }

  private loadAllMembersPaymentRows(): void {
    const subgroupDescriptors = this.feeGroups.flatMap(group =>
      group.subgroups.map(subgroup => ({
        groupId: group.id,
        groupName: group.name,
        subgroupId: subgroup.id,
        subgroupName: subgroup.name,
        mentorName: subgroup.mentorName || '—',
      }))
    );

    if (subgroupDescriptors.length === 0) {
      this.allMembersPaymentRows = [];
      this.allMembersPaymentVisibleCount = this.loadMorePageSize;
      return;
    }

    const memberRequests = subgroupDescriptors.map(item =>
      this.feeService.getMembersBySubgroup(item.subgroupId).pipe(catchError(() => of([] as FeeMemberResponse[])))
    );

    forkJoin(memberRequests)
      .pipe(takeUntil(this.destroy$))
      .subscribe(memberLists => {
        const rowsSeed = subgroupDescriptors.flatMap((descriptor, index) =>
          memberLists[index].map(member => ({ member, descriptor }))
        );

        if (rowsSeed.length === 0) {
          this.allMembersPaymentRows = [];
          this.allMembersPaymentVisibleCount = this.loadMorePageSize;
          return;
        }

        const uniqueMemberIds = [...new Set(rowsSeed.map(s => s.member.id))];
        const profileRequests = uniqueMemberIds.map(id =>
          this.feeService.getMemberProfile(id).pipe(catchError(() => of(null as MemberProfileResponse | null)))
        );

        forkJoin(profileRequests)
          .pipe(takeUntil(this.destroy$))
          .subscribe(profiles => {
            const profileMap = new Map<number, MemberProfileResponse | null>();
            uniqueMemberIds.forEach((id, i) => profileMap.set(id, profiles[i]));

            this.allMembersPaymentRows = rowsSeed.flatMap(seed => {
              const profile = profileMap.get(seed.member.id);
              const payments = profile?.paymentHistory?.filter(
                p => p.subgroupId === seed.descriptor.subgroupId
                  && (p.status === FEE_PAYMENT_STATUS.PAID || p.status === FEE_PAYMENT_STATUS.PENDING)
              ) ?? [];
              return payments.map(payment => ({
                memberId: seed.member.id,
                memberName: seed.member.name,
                memberActive: seed.member.active ?? true,
                passportId: seed.member.passportId || '—',
                groupId: seed.descriptor.groupId,
                groupName: seed.descriptor.groupName,
                subgroupId: seed.descriptor.subgroupId,
                subgroupName: seed.descriptor.subgroupName,
                mentorName: seed.descriptor.mentorName,
                period: payment.month,
                paid: payment.total,
                due: payment.due,
                status: payment.status as string,
              }));
            });
            this.allMembersPaymentVisibleCount = this.loadMorePageSize;
            this.mentorsVisibleCount = this.loadMorePageSize;
          });
      });
  }

  openMemberProfile(item: MemberRow): void {
    this.router.navigate(['/fees/user-profile', item.id], {
      queryParams: {industryId: this.industryId, subgroupId: item.subgroupId}
    });
  }

  openMemberFromPaymentRow(memberId: number, subgroupId?: number): void {
    this.router.navigate(['/fees/user-profile', memberId], {
      queryParams: {industryId: this.industryId, subgroupId}
    });
  }


  toggleMemberActive(item: MemberRow, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const newActive = checked;
    const prevActive = item.active ?? true;
    item.active = newActive;
    this.feeService.setEnrollmentActive(item.id, item.subgroupId, newActive).pipe(takeUntil(this.destroy$)).subscribe({
      error: () => { item.active = prevActive; }
    });
  }

  private toMemberRow(member: FeeMemberResponse): MemberRow {
    return {
      id: member.id,
      subgroupId: member.subgroupId,
      name: member.name,
      frequency: member.frequency,
      discount: '—',
      due: '0',
      total: (member.currentPeriodPaid ?? 0).toLocaleString(),
      status: member.currentPeriodStatus ?? FEE_PAYMENT_STATUS.PENDING,
      active: member.active ?? true
    };
  }

  private resolveSubgroupStatus(sub: FeeSubgroupResponse): OverviewStatus {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const start = parseApiDateString(sub.startDate);
    const end = parseApiDateString(sub.endDate);

    if (start && now < start) return 'NOT_STARTED';
    if (end && now > end) return 'COMPLETED';
    return 'IN_PROGRESS';
  }

  private parsePeriodToDate(period: string | null | undefined): Date | null {
    if (!period) return null;
    const cleaned = period.replace(/week of/i, '').trim();
    if (/^\d{4}$/.test(cleaned)) {
      return new Date(Number(cleaned), 0, 1);
    }
    const parsed = new Date(cleaned);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private startOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  private endOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  private matchesOverviewQuery(row: OverviewRow, query: string): boolean {
    return row.subgroupName.toLowerCase().includes(query)
      || row.parentGroupName.toLowerCase().includes(query)
      || row.mentorName.toLowerCase().includes(query)
      || row.startEndDate.toLowerCase().includes(query)
      || this.statusLabel(row.status).toLowerCase().includes(query);
  }

  private discountLabel(subgroup: FeeSubgroupResponse): string {
    if (!subgroup.discountValue) return '0';
    if (subgroup.discountType === 'PERCENT') return `${subgroup.discountValue}%`;
    return `${subgroup.discountValue} AMD`;
  }

  private getMentorAmountTotal(mentorName: string): number {
    const key = (mentorName || '').trim().toLowerCase();
    return this.allMembersPaymentRows
      .filter(row => (row.mentorName || '').trim().toLowerCase() === key && row.status === FEE_PAYMENT_STATUS.PAID)
      .reduce((sum, row) => sum + row.paid + row.due, 0);
  }

  private reloadFeeGroups(): void {
    this.feeService.getFeeGroups(this.industryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(groups => {
        this.feeGroups = groups;
        this.resetVisibleCounts();
        this.loadAllMembersPaymentRows();
      });
  }

  private loadDashboard(): void {
    if (!this.industryId) return;
    this.feeService.getDashboard(this.selectedDashboardPeriod.value, this.industryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(dashboard => {
        this.dashboard = dashboard;
      });
  }

  private resetVisibleCounts(): void {
    this.allMembersPaymentVisibleCount = this.loadMorePageSize;
    this.subgroupsVisibleCount = this.loadMorePageSize;
    this.groupsVisibleCount = this.loadMorePageSize;
    this.mentorsVisibleCount = this.loadMorePageSize;
    this.subgroupMembersVisibleCount = this.loadMorePageSize;
  }

  private findSubgroupById(subgroupId: number): FeeSubgroupResponse | null {
    for (const group of this.feeGroups) {
      const subgroup = group.subgroups.find(s => s.id === subgroupId);
      if (subgroup) return subgroup;
    }
    return null;
  }

  private findGroupBySubgroupId(subgroupId: number): FeeGroupResponse | null {
    for (const group of this.feeGroups) {
      if (group.subgroups.some(s => s.id === subgroupId)) return group;
    }
    return null;
  }

  isGroupCompleted(groupId: number): boolean {
    const group = this.feeGroups.find(g => g.id === groupId);
    if (!group || group.subgroups.length === 0) return false;
    return group.subgroups.every(subgroup => this.resolveSubgroupStatus(subgroup) === 'COMPLETED');
  }

  isSubgroupCompleted(subgroupId: number): boolean {
    const subgroup = this.findSubgroupById(subgroupId);
    if (!subgroup) return false;
    return this.resolveSubgroupStatus(subgroup) === 'COMPLETED';
  }

  private hasLinkedCard(industry: UserIndustryResponse | null): boolean {
    return !!industry
      && !!industry.cardId
      && industry.cardId.toString().trim().length > MIN_CARD_ID_LENGTH;
  }

  private loadInvoiceSummaryTitle(): void {
    this.translateService
      .get('fee.invoice_summary')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(title => {
        this.invoiceSummaryTitle = title;
      });
  }
}
